import { useQuery } from '@tanstack/react-query';
import { TZDate } from '@date-fns/tz';
import { useApi } from '../../hooks';
import { CareModel } from '../../models/user';
import { formatDateToDayOnly } from '../../components/AppointmentsPage/utils';
import {
  AvailabilityEntry,
  MeetingInfo,
  MeetingType as MeetingTypeId,
} from '../../models/meeting';
import qs from 'qs';

type CoachAvailabilityResponse = {
  meeting_type: MeetingInfo;
  availability: AvailabilityEntry[];
};

type Props = {
  care_model?: CareModel;
  coach_uuid?: string;
  endDate?: TZDate;
  meetingType?: MeetingTypeId | null;
  startDate?: TZDate;
  timezone?: string;
};

export const useCoachAvailability = ({
  care_model,
  coach_uuid,
  endDate,
  meetingType: appt_type,
  startDate,
  timezone,
}: Props) => {
  const { api } = useApi();

  const fetchAvailability = async (): Promise<CoachAvailabilityResponse> => {
    if (!startDate || !endDate) {
      return { availability: [] } as unknown as CoachAvailabilityResponse;
    }

    const start_date = formatDateToDayOnly(startDate);
    const end_date = formatDateToDayOnly(endDate);

    const queryStr = qs.stringify({
      appt_type,
      end_date,
      start_date,
      timezone,
    });

    return api({
      route: `/coach/${coach_uuid}/availability${
        queryStr ? `?${queryStr}` : ''
      }`,
    });
  };

  const { data, isInitialLoading, isLoading, isError } = useQuery<
    CoachAvailabilityResponse,
    Error
  >({
    queryKey: [
      'public-coach-availability',
      coach_uuid,
      endDate,
      appt_type,
      startDate,
      timezone,
    ],
    queryFn: fetchAvailability,
    enabled:
      !!care_model &&
      !!coach_uuid &&
      !!endDate &&
      !!appt_type &&
      !!startDate &&
      !!timezone,
    refetchOnWindowFocus: false,
  });

  return {
    isLoading: isInitialLoading || isLoading,
    isError,
    data,
  };
};
